import React, { useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCornerDownRight, FiSearch } from 'react-icons/fi';
import { adminApi } from '../adminApi';
import { showDelete, showAlert } from '../utils/dialog';

const EMPTY = { name: '', description: '', parent_id: '', image_url: '' };

function buildTree(categories, parentId = null) {
  return categories
    .filter(c => (c.parent_id ?? null) === parentId)
    .map(c => ({ ...c, children: buildTree(categories, c.id) }));
}

// Collect all descendant IDs of a node (to block selecting self or own children as parent)
function getDescendantIds(categories, id) {
  const children = categories.filter(c => c.parent_id === id);
  return children.flatMap(c => [c.id, ...getDescendantIds(categories, c.id)]);
}

// Flatten tree for parent dropdown with depth-based indent labels
function flattenForSelect(nodes, depth = 0) {
  return nodes.flatMap(node => [
    { id: node.id, label: ' '.repeat(depth * 4) + (depth > 0 ? '› ' : '') + node.name },
    ...flattenForSelect(node.children, depth + 1),
  ]);
}

function CategoryRow({ node, depth, categories, onEdit, onAdd, onDelete }) {
  return (
    <>
      <tr className={depth === 0 ? 'cat-parent-row' : 'cat-sub-row'}>
        <td />
        <td>
          {node.image_url
            ? <img src={node.image_url} alt={node.name} style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
            : <div style={{ width: 38, height: 38, borderRadius: 4, background: 'var(--border)' }} />
          }
        </td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: depth * 22 }}>
            {depth > 0 && <FiCornerDownRight size={13} color="var(--text-muted)" style={{ flexShrink: 0 }} />}
            <span className={depth === 0 ? 'bold' : ''} style={{ fontWeight: depth === 0 ? 700 : 500 }}>{node.name}</span>
            {node.children.length > 0 && (
              <span className="cat-sub-count">{node.children.length} sub</span>
            )}
          </div>
        </td>
        <td style={{ color: 'var(--text-muted)', fontSize: '.8125rem' }}>{node.description || '—'}</td>
        <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: '.8125rem' }}>
          {new Date(node.created_at).toLocaleDateString()}
        </td>
        <td>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn-admin btn-admin-ghost btn-admin-sm" title="Add subcategory" onClick={() => onAdd(node.id)}>
              <FiPlus size={13} />
            </button>
            <button className="btn-admin btn-admin-ghost btn-admin-sm" onClick={() => onEdit(node)}>
              <FiEdit2 size={13} />
            </button>
            <button className="btn-admin btn-admin-danger btn-admin-sm" onClick={() => onDelete(node.id, node.children.length > 0)}>
              <FiTrash2 size={13} />
            </button>
          </div>
        </td>
      </tr>
      {node.children.map(child => (
        <CategoryRow key={child.id} node={child} depth={depth + 1} categories={categories} onEdit={onEdit} onAdd={onAdd} onDelete={onDelete} />
      ))}
    </>
  );
}

function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(EMPTY);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [imgUploading, setImgUploading] = useState(false);
  const [search,     setSearch]     = useState('');

  const load = () => {
    setLoading(true);
    adminApi.getCategories()
      .then(r => setCategories(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd = (parentId = '') => {
    setEditing(null);
    setForm({ ...EMPTY, parent_id: parentId });
    setError('');
    setModal(true);
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name, description: c.description || '', parent_id: c.parent_id || '', image_url: c.image_url || '' });
    setError('');
    setModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      setForm(f => ({ ...f, image_url: res.data.url }));
    } catch { showAlert('Image upload failed'); }
    finally { setImgUploading(false); }
  };

  const handleDelete = async (id, hasSubs) => {
    const msg = hasSubs
      ? 'This category has subcategories. Delete it anyway?'
      : 'Delete this category?';
    if (!await showDelete(msg)) return;
    await adminApi.deleteCategory(id).catch(() => {});
    load();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Category name is required');
    setError(''); setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description || null,
      image_url: form.image_url || null,
      parent_id: form.parent_id ? Number(form.parent_id) : null,
    };
    try {
      editing ? await adminApi.updateCategory(editing, payload) : await adminApi.createCategory(payload);
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Build tree for display
  const tree = buildTree(categories);

  const searchQ = search.trim().toLowerCase();
  const filteredCats = searchQ
    ? categories.filter(c => c.name.toLowerCase().includes(searchQ))
    : null;

  const parentName = (parentId) => {
    if (!parentId) return null;
    const p = categories.find(c => c.id === parentId);
    return p ? p.name : null;
  };

  // Build flat list for parent dropdown (exclude self + descendants)
  const forbiddenIds = editing ? new Set([editing, ...getDescendantIds(categories, editing)]) : new Set();
  const allTree = buildTree(categories);
  const parentOptions = flattenForSelect(allTree).filter(o => !forbiddenIds.has(o.id));

  const modalTitle = editing ? 'Edit Category' : form.parent_id ? 'Add Subcategory' : 'Add Category';

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h3>Categories ({categories.length})</h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div className="admin-search">
              <FiSearch size={14} color="var(--text-muted)" />
              <input
                placeholder="Search categories…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <button className="btn-admin btn-admin-primary" onClick={() => openAdd()}>
              <FiPlus size={15} /> Add Category
            </button>
          </div>
        </div>

        <div className="admin-table-wrap">
          {loading
            ? <div className="admin-loading"><div className="admin-spinner" />Loading…</div>
            : categories.length === 0
              ? <div className="admin-empty">No categories yet</div>
              : filteredCats
                ? (
                  <table className="admin-table">
                    <thead>
                      <tr><th style={{width:36}}>#</th><th style={{width:52}}>Image</th><th>Name</th><th>Description</th><th>Created</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {filteredCats.length === 0
                        ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>No categories found</td></tr>
                        : filteredCats.map(cat => (
                          <tr key={cat.id}>
                            <td />
                            <td>
                              {cat.image_url
                                ? <img src={cat.image_url} alt={cat.name} style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                                : <div style={{ width: 38, height: 38, borderRadius: 4, background: 'var(--border)' }} />
                              }
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span className="bold">{cat.name}</span>
                                {parentName(cat.parent_id) && (
                                  <span className="cat-sub-count">{parentName(cat.parent_id)}</span>
                                )}
                              </div>
                            </td>
                            <td style={{ color: 'var(--text-muted)', fontSize: '.8125rem' }}>{cat.description || '—'}</td>
                            <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: '.8125rem' }}>
                              {new Date(cat.created_at).toLocaleDateString()}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button className="btn-admin btn-admin-ghost btn-admin-sm" title="Add subcategory" onClick={() => openAdd(cat.id)}><FiPlus size={13} /></button>
                                <button className="btn-admin btn-admin-ghost btn-admin-sm" onClick={() => openEdit(cat)}><FiEdit2 size={13} /></button>
                                <button className="btn-admin btn-admin-danger btn-admin-sm" onClick={() => handleDelete(cat.id, categories.some(c => c.parent_id === cat.id))}><FiTrash2 size={13} /></button>
                              </div>
                            </td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                )
                : (
                  <table className="admin-table">
                    <thead>
                      <tr><th style={{width:36}}>#</th><th style={{width:52}}>Image</th><th>Name</th><th>Description</th><th>Created</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {tree.map(node => (
                        <CategoryRow
                          key={node.id}
                          node={node}
                          depth={0}
                          categories={categories}
                          onEdit={openEdit}
                          onAdd={openAdd}
                          onDelete={handleDelete}
                        />
                      ))}
                    </tbody>
                  </table>
                )
          }
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{modalTitle}</h3>
              <button className="modal-close" onClick={() => setModal(false)}><FiX size={16} /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {error && <div className="admin-error" style={{ marginBottom: 12 }}>{error}</div>}
                <div className="admin-form-group">
                  <label>Parent Category <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank for top-level)</span></label>
                  <select value={form.parent_id} onChange={e => setForm({ ...form, parent_id: e.target.value })}>
                    <option value="">— Top-level category —</option>
                    {parentOptions.map(o => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Name *</label>
                  <input value={form.name} onChange={e => { setForm({ ...form, name: e.target.value }); setError(''); }} placeholder="Category name" />
                </div>
                <div className="admin-form-group">
                  <label>Description</label>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Optional description" />
                </div>
                <div className="admin-form-group">
                  <label>Image</label>
                  {form.image_url && (
                    <div style={{ marginBottom: 8 }}>
                      <img src={form.image_url} alt="preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={handleImageUpload} disabled={imgUploading} />
                  {imgUploading && <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}> Uploading…</span>}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-admin btn-admin-ghost" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn-admin btn-admin-primary" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCategories;
